import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { CalendarRange } from "lucide-react";
import { toast } from "sonner";
import { runRecipeInterval, waitForRun } from "@/lib/api";

function yesterdayIso() {
  const d = new Date(); d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

/**
 * Single-run with a date interval: start_date + end_date are substituted into
 * the recipe (date_role + ${DATE_START}/${DATE_END} placeholders). One scrape,
 * one table.
 */
export default function IntervalRunDialog({ recipe, onRunning }) {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState(yesterdayIso());
  const [end, setEnd] = useState(yesterdayIso());
  const [feedCutting, setFeedCutting] = useState(true);
  const [running, setRunning] = useState(false);
  const nav = useNavigate();

  const supportsInterval = useMemo(() => {
    const steps = recipe.steps || [];
    const json = JSON.stringify(steps);
    return (
      json.includes("${DATE_START") ||
      json.includes("${DATE_END") ||
      steps.some((s) => s.type === "navigate") ||
      steps.some((s) => s.type === "fill" && s.is_date && (s.date_role === "start" || s.date_role === "end"))
    );
  }, [recipe.steps]);

  async function onRun() {
    if (!start || !end) return toast.error("Define ambas as datas.");
    if (new Date(end) < new Date(start)) return toast.error("A data final tem de ser depois da inicial.");
    setRunning(true);
    onRunning?.(true);
    try {
      const started = await runRecipeInterval(recipe.id, start, end, feedCutting);
      toast.info("Execução iniciada em segundo plano…");
      const run = await waitForRun(started);
      if (run.status === "error") {
        toast.error(`A captura falhou: ${run.error || "erro desconhecido"}`);
      } else {
        toast.success(`Recolhidas ${run.rows?.length || 0} linhas no intervalo.`);
      }
      setOpen(false);
      nav(`/runs/${run.id}`);
    } catch (e) {
      toast.error(`Falha: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRunning(false);
      onRunning?.(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="recipe-card-interval-run-button" title="Correr com intervalo (1 captura, 2 datas)">
          <CalendarRange className="h-3.5 w-3.5 mr-1"/> Intervalo
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Captar dados num intervalo</DialogTitle>
          <DialogDescription>
            Uma única execução: a receita substitui <span className="mono">{"${DATE_START}"}</span> e <span className="mono">{"${DATE_END}"}</span> (e os passos marcados como data com papel “início/fim”) e devolve toda a tabela do intervalo.
          </DialogDescription>
        </DialogHeader>

        {!supportsInterval && (
          <div className="text-xs rounded-lg border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 p-3 text-foreground">
            ⚠️ Esta receita ainda não tem placeholders de intervalo nem passo de navegação. Para que o intervalo funcione, edita a receita e adiciona pelo menos um destes:
            <ul className="list-disc ml-4 mt-1">
              <li>um passo <span className="mono">navigate</span> com URL contendo <span className="mono">{"${DATE_START:fmt}"}</span> e <span className="mono">{"${DATE_END:fmt}"}</span>;</li>
              <li>ou 2 campos <span className="mono">fill</span> marcados como <em>data</em> com papel <em>início</em> e <em>fim</em>.</li>
            </ul>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="interval-start" className="text-xs">Data início</Label>
            <Input id="interval-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="interval-start-date-input"/>
          </div>
          <div>
            <Label htmlFor="interval-end" className="text-xs">Data fim</Label>
            <Input id="interval-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="interval-end-date-input"/>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" type="button" onClick={() => { const d = yesterdayIso(); setStart(d); setEnd(d); }}>Ontem</Button>
          <Button size="sm" variant="outline" type="button" onClick={() => { const d = new Date(); d.setDate(d.getDate() - 7); setStart(d.toISOString().slice(0, 10)); setEnd(yesterdayIso()); }}>Últimos 7 dias</Button>
          <Button size="sm" variant="outline" type="button" onClick={() => { const d = new Date(); d.setDate(d.getDate() - 30); setStart(d.toISOString().slice(0, 10)); setEnd(yesterdayIso()); }}>Últimos 30 dias</Button>
        </div>

        {recipe.feed_cutting_simulator && (
          <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2">
            <div className="text-xs">
              <div className="font-medium">Alimentar Máquina de Corte</div>
              <div className="text-muted-foreground">Linhas com data são distribuídas por dia (se a tabela tiver coluna “Data”).</div>
            </div>
            <Switch checked={feedCutting} onCheckedChange={setFeedCutting} data-testid="interval-feed-cutting"/>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={running}>Cancelar</Button>
          <Button onClick={onRun} disabled={running} data-testid="interval-run-confirm">{running ? "A correr… (pode demorar)" : "Correr intervalo"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
